import axios from 'axios'
import { AddGroupContextProps } from 'components/popups/addGroup/AddGroupProvider'
import { performUpdateGroupSettings } from 'components/popups/groupInfo/SettingsForCalendarSection'
import {
  doGetChanges,
  processDownloadedData
} from 'components/providers/GetChangesProvider'
import dayjs from 'dayjs'
import { firebaseConfig } from 'firebase-config'
import { t } from 'i18next'
import { GroupPrivacy } from 'model/models/Group'
import Participant from 'model/models/Participant'
import Api from 'services/Api'
import Database from 'services/db/Database'
import { Account } from 'services/db/Queries/AccountQueries'
import {
  localGroupById,
  ParticipantsForGroup
} from 'services/db/Queries/GroupQueries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  AccountDoc,
  ChangeDocsByTypes,
  GroupBusinessInfo,
  GroupDoc,
  GroupParticipant,
  GroupParticipantStatus,
  GroupParticipantType,
  GroupSettings,
  GroupStatus,
  ParticipantDataDoc
} from 'types/api/changes'
import {
  AddParticipantData,
  GROUP_INFO_ACTION_TYPES,
  ResetConvertedGroupData,
  UpdateBusinessInfoData,
  UpdateGroupColorData,
  UpdateGroupData,
  UpdateGroupSettingsData,
  UpdateLinkData,
  UpdateParticipantData,
  UpdatePersonalGroupSyncStatusData,
  UpdatePhotoData,
  UpdateTitleData
} from 'types/api/group'
import {
  InheritanceStatusResponse,
  InheritFromGroupsRequest,
  InheritFromGroupsResponse
} from 'types/api/group'
import { CollectionType, Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { isGroupArchived } from 'utils/api/userSettings'
import { is24meApp } from 'utils/appType'
import { sanitizeGroupInheritanceFields } from 'utils/calendarInheritance'
import { getOrUpdateDeviceId } from 'utils/device'
import { isTestEnv } from 'utils/env'
import { base64RemoveMetadata } from 'utils/file'
import {
  groupIdToProvider,
  PersonalGroupSyncStatus,
  ProviderType
} from 'utils/groups'
import { isTrulyEmpty, prepareDocForSaving, removePrefix } from 'utils/object'
import { uuid } from 'utils/uuid'

import { disableDeepLink, generateDeepLink } from './deeplinks'
import { triggerInheritanceSync } from './inheritanceSync'
const API_GROUP_URL = '/v1/groups/'
const API_INHERIT_FROM_GROUPS = '/v1/groups/inheritfromgroups'
const API_INHERITANCE_STATUS = '/v1/groups/inheritance/status'
const API_PROFILES = '/v1/profiles'
const API_JOIN_GROUP = '/v1/groups/join'
const API_DOWNGRADE_GROUP = (groupId: string) =>
  `/v1/groups/${groupId}/downgrade`

const API_SEARCH_GROUP = '/v1/groups/search'
const API_TRANSFER_OWNERSHIP = (groupId: string) =>
  `/v1/groups/${groupId}/owner`
const API_DELETE_GROUP = (groupId: string) => `/v1/groups/${groupId}`

export async function hardDeleteGroupFromServer(groupId: string) {
  return await Api.delete(API_DELETE_GROUP(groupId), {})
}

export const genericUpdateFields = () => ({
  DeviceChangeID: LocalStorage.get(LocalStorageKey.DEVICE_ID),
  UserID: LocalStorage.get(LocalStorageKey.USER_ID)
})

export async function inheritFromGroups(
  payload: InheritFromGroupsRequest
): Promise<InheritFromGroupsResponse> {
  const deviceChangeId = LocalStorage.get(LocalStorageKey.DEVICE_ID)
  const response = await Api.post(API_INHERIT_FROM_GROUPS, {
    ...payload,
    DeviceChangeID: deviceChangeId
  })
  const result = response.data as InheritFromGroupsResponse
  const groups = Object.fromEntries(
    (result.groups ?? []).map((group) => [group._id ?? '', group])
  )

  if (Object.keys(groups).length > 0) {
    await processDownloadedData({
      Group: groups,
      GroupEvent: {},
      Account: {},
      MasterLabel: {},
      Profile: {},
      UserSettings: {},
      Task: {},
      Note: {}
    })
  }

  if (result.success && (result.errors?.length ?? 0) === 0)
    triggerInheritanceSync(deviceChangeId)

  return result
}

export async function inheritanceStatus(
  subjectGroupIds: string[]
): Promise<InheritanceStatusResponse> {
  const uniqueSubjectGroupIds = Array.from(
    new Set(subjectGroupIds.filter((groupId) => groupId.length > 0))
  )

  const response = await Api.post(API_INHERITANCE_STATUS, {
    subjectGroupIds: uniqueSubjectGroupIds
  })

  return response.data as InheritanceStatusResponse
}

export async function addGroupToServer(
  pendingGroup: Model<GroupDoc> | GroupDoc,
  addGroupContextProps: AddGroupContextProps
): Promise<GroupDoc> {
  //adding group to server first
  const deviceId = LocalStorage.get(LocalStorageKey.DEVICE_ID)

  const channelGroupsSettings: GroupSettings = {
    IsAllParticipantsCanAddItems: '0',
    IsAllParticipantsCanAddParticipants: '0',
    IsAllParticipantsCanEditGroupMetadata: '0',
    IsAllParticipantsCanEditOnlyTheirEvents: '0',
    IsAllParticipantsCanSeeFreeBusyOnly: '0'
  }

  const params = {
    DeviceChangeID: deviceId,
    GroupSettings: addGroupContextProps.isCalendarChannel
      ? channelGroupsSettings
      : pendingGroup.GroupSettings,
    PrivacyMode: addGroupContextProps.isCalendarChannel
      ? GroupPrivacy.CHANNEL
      : GroupPrivacy.PRIVATE,
    GroupColor: addGroupContextProps.color,
    Version: '3',
    Status: '1',
    PersonalGroupSyncStatus: PersonalGroupSyncStatus.PENDING,
    Name: addGroupContextProps.title,
    UserID: LocalStorage.get(LocalStorageKey.USER_ID),
    Photo: base64RemoveMetadata(addGroupContextProps.photo),
    CalendarEmail: pendingGroup.CalendarEmail,
    CalendarType: pendingGroup.CalendarType
  }
  const response = await Api.post(API_GROUP_URL, params)
  const data = response.data
  const groupId = data['_id']
  LocalStorage.set(LocalStorageKey.ADDING_GROUP, groupId)
  await Database.write(async () => {
    return await pendingGroup.update(() => {
      pendingGroup.PrivacyMode = data['PrivacyMode']
      pendingGroup._raw['_id'] = groupId
      pendingGroup._raw['_rev'] = data['_rev']
      pendingGroup._raw['Name'] = data['Name']
      pendingGroup._raw['_message'] = t('youCreatedCalendar')
      pendingGroup._raw['CalendarEmail'] = pendingGroup.CalendarEmail
      pendingGroup._raw['CalendarType'] = pendingGroup.CalendarType
      pendingGroup._raw['PersonalGroupSyncStatus'] =
        pendingGroup.PersonalGroupSyncStatus ?? PersonalGroupSyncStatus.PENDING
      pendingGroup.Version = data['Version']
    })
  })

  try {
    await generateLinkForGroup(pendingGroup)
  } catch (e) {
    Logger.red('Error while generating link for new group', e)
  }

  addGroupContextProps.setLink(pendingGroup.PrivateLinkUrl)

  return pendingGroup
}

export async function generateLinkForGroup(group: Model<GroupDoc> | GroupDoc) {
  const groupId = group._id ?? ''
  const deviceId = getOrUpdateDeviceId()
  const password = uuid().toString().replace('-', '').replace('/', '')

  //creating link to update
  const link = await makeLinkForGroup(groupId, password, group).then(
    (response) => {
      const updatedGroupWithLink = response

      // updating local storage after link synced with server
      return Database.write(async () => {
        return group.update(() => {
          group.Photo = updatedGroupWithLink.Photo
          group.Participants = updatedGroupWithLink.Participants
          group.Name = updatedGroupWithLink.Name
          group.GroupColor = updatedGroupWithLink.GroupColor
          group.PrivateLinkUrl = updatedGroupWithLink.PrivateLinkUrl
          group.PrivateLinkPassword = updatedGroupWithLink.PrivateLinkPassword
          group._raw['_id'] = group._id // to preserve server ids locally
          group.LastUpdate = dayjs().unix().toString()
          group.OwnerID = updatedGroupWithLink.OwnerID
          group.GroupSettings = updatedGroupWithLink.GroupSettings
        })
      })
    }
  )

  //updating group with link
  return link
}

export async function updateLinkOnServer(
  updateLinkData: UpdateLinkData,
  group: Model<GroupDoc>
): Promise<GroupDoc> {
  if (updateLinkData.PrivateLinkUrl === '') {
    // we need to disable the link
    if (group.PrivateLinkUrl.includes('link.groupcal'))
      return await disableDeepLink(updateLinkData.GroupID).then((response) => {
        const updatedGroupWithLink = response

        // updating local storage after link synced with server
        return Database.write(async () => {
          return group.update(() => {
            group.Photo = updatedGroupWithLink.Photo
            group.Participants = updatedGroupWithLink.Participants
            group.Name = updatedGroupWithLink.Name
            group.GroupColor = updatedGroupWithLink.GroupColor
            group.PrivateLinkUrl = updatedGroupWithLink.PrivateLinkUrl
            group.PrivateLinkPassword = updateLinkData.PrivateLinkPassword
            group._raw['_id'] = group._id // to preserve server ids locally
            group.LastUpdate = dayjs().unix().toString()
            group.OwnerID = updatedGroupWithLink.OwnerID
            group.GroupSettings = updatedGroupWithLink.GroupSettings
            group.LastUpdateMessage = {
              type: t('youAddedThisCalendar')
            }
            group._raw['_sortOrder'] = dayjs().unix() + 10
          })
        })
      })
  }

  return await Api.post(`${API_GROUP_URL}${group._id}`, {
    ...updateLinkData
  }).then((response) => {
    const updatedGroupWithLink = response.data as unknown as GroupDoc

    // updating local storage after link synced with server
    return Database.write(async () => {
      return group.update(() => {
        group.Photo = updatedGroupWithLink.Photo
        group.Participants = updatedGroupWithLink.Participants
        group.Name = updatedGroupWithLink.Name
        group.GroupColor = updatedGroupWithLink.GroupColor
        group.PrivateLinkUrl = updatedGroupWithLink.PrivateLinkUrl
        group.PrivateLinkPassword = updateLinkData.PrivateLinkPassword
        group._raw['_id'] = group._id // to preserve server ids locally
        group.LastUpdate = dayjs().unix().toString()
        group.OwnerID = updatedGroupWithLink.OwnerID
        group.GroupSettings = updatedGroupWithLink.GroupSettings
        group.LastUpdateMessage = {
          type: t('youAddedThisCalendar')
        }
        group._raw['_sortOrder'] = dayjs().unix()
      })
    })
  })
}

async function makeLinkForGroup(
  groupId: string,
  password: string,
  group: Model<GroupDoc> | GroupDoc
): Promise<GroupDoc> {
  // TODO: Uncomment when deep link migration is ready on all clients/server

  return generateDeepLink(
    {
      PrivateLinkPassword: password
    },
    groupId
  ).catch((error) => {
    Logger.red('Error while generating link', error)
    throw error
  })

  const linksInstance = axios.create({
    baseURL: 'https://firebasedynamiclinks.googleapis.com/v1'
  })

  const ofl = `${process.env.REACT_APP_LINK_DOMAIN}/calendar/${groupId}⁠${password}`
  const ibi = isTestEnv() ? 'GroupCal24meDev' : 'GroupCal24me'
  const ius = isTestEnv() ? 'groupcaldev' : 'groupcal'
  const isi = '1472335927'
  const apn = isTestEnv() ? 'app.groupcal.www.debug' : 'app.groupcal.www'
  const link = (
    await linksInstance.post(`/shortLinks?key=${firebaseConfig.apiKey}`, {
      longDynamicLink: `https://groupcal.page.link/?link=https://${groupId}?${password}&ibi=${ibi}&ius=${ius}&isi=${isi}&ofl=${ofl}&apn=${apn}`
    })
  ).data

  const parts = link.shortLink.split('/')
  const originalLink = parts[parts.length - 1]
  const mobileOfl = `${process.env.REACT_APP_LINK_DOMAIN}/calendar/${groupId}⁠${password}⁠${originalLink}`
  const shortLink = (
    await linksInstance.post(`/shortLinks?key=${firebaseConfig.apiKey}`, {
      longDynamicLink: `https://groupcal.page.link/?link=https://${groupId}?${password}&ibi=${ibi}&ius=${ius}&isi=${isi}&ofl=${mobileOfl}&apn=${apn}&afl=${mobileOfl}&ifl=${mobileOfl}`
    })
  ).data.shortLink
  return updateLinkOnServer(
    {
      PrivateLinkUrl: shortLink,
      PrivateLinkPassword: password,
      ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
      UserID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
      GroupID: group._id ?? '',
      DeviceChangeID: getOrUpdateDeviceId()
    },
    group as Model<GroupDoc>
  )
}

export async function updateGroupColor(color: string, group: Model<GroupDoc>) {
  const updateColor: UpdateGroupColorData = {
    ...genericUpdateFields(),
    ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
    GroupColor: color,
    GroupID: group._id
  }

  return updateGroupOnServer(updateColor, group)
}

export async function updateGroupTitle(
  title: string,
  group: Model<GroupDoc>
): Promise<void> {
  const updateTitle: UpdateTitleData = {
    ...genericUpdateFields(),
    ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
    Name: title,
    GroupID: group._id
  }

  return updateGroupOnServer(updateTitle, group)
}

export async function updateGroupBI(
  biText: string,
  group: Model<GroupDoc>
): Promise<void> {
  const newBusinessInfo: GroupBusinessInfo = {
    Text: group.BusinessInfo?.Text ?? '',
    Active: group.BusinessInfo?.Text ? '1' : '0'
  }
  newBusinessInfo.Text = biText
  newBusinessInfo.Active = '1'
  const updateTitle: UpdateBusinessInfoData = {
    ...genericUpdateFields(),
    ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
    BusinessInfo: newBusinessInfo,
    GroupID: group._id
  }

  return updateGroupOnServer(updateTitle, group)
}

export async function resetConvertedGroupState(group: Model<GroupDoc>) {
  if (isTrulyEmpty(group._rev)) return Promise.resolve()

  // set all participant status to removed
  const participantsToUpdate: Record<string, GroupParticipant> = {}
  Object.keys(group.Participants).forEach((participantKey) => {
    const participant = group.Participants[participantKey]
    if (
      participant.Status !== GroupParticipantStatus.INACTIVE &&
      participantKey !== group.OwnerID
    ) {
      participantsToUpdate[participantKey] = {
        ...participant,
        Status: GroupParticipantStatus.INACTIVE,
        Type: GroupParticipantType.REGULAR,
        RemovedBy: LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      }
    }
  })
  const pendingParticipantsToUpdate: Record<string, GroupParticipant> = {}
  Object.keys(group.PendingParticipants).forEach((participantKey) => {
    const participant = group.PendingParticipants[participantKey]
    if (participant.Status !== GroupParticipantStatus.INACTIVE) {
      pendingParticipantsToUpdate[participantKey] = {
        ...participant,
        Status: GroupParticipantStatus.INACTIVE,
        Type: GroupParticipantType.REGULAR,
        RemovedBy: LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      }
    }
  })

  const resetState: ResetConvertedGroupData = {
    ...genericUpdateFields(),
    ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
    GroupID: group._id,
    PrivateLinkUrl: '',
    PrivateLinkPassword: '',
    PersonalGroupSyncStatus: PersonalGroupSyncStatus.PRIVATE, // private group without sync
    Participants: participantsToUpdate,
    PendingParticipants: pendingParticipantsToUpdate // removing all pending participants just in case
  }
  // updating local storage after link synced with server
  Database.write(async () => {
    return group.update(() => {
      group.PrivateLinkUrl = ''
      group.PrivateLinkPassword = ''
      group.PersonalGroupSyncStatus = PersonalGroupSyncStatus.PRIVATE // private group without sync
      group._raw['_id'] = group._id // to preserve server ids locally
      group.LastUpdate = (dayjs().unix() + 5).toString()
      group._raw['_sortOrder'] = Number(group.LastUpdate)
      group.LastUpdateMessage = {
        type: t('youAddedThisCalendar')
      }
    })
  })

  return updateGroupOnServer(resetState, group)
}

export async function updateGroupPersonalSyncStatus(
  PersonalGroupSyncStatus: string,
  group: Model<GroupDoc> | GroupDoc
): Promise<void> {
  const updatePersonalSyncStatus: UpdatePersonalGroupSyncStatusData = {
    ...genericUpdateFields(),
    ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
    PersonalGroupSyncStatus: PersonalGroupSyncStatus,
    GroupID: group._id ?? ''
  }

  return updateGroupOnServer(updatePersonalSyncStatus, group)
}

export async function updateGroupPhoto(
  photo: string,
  group: Model<GroupDoc>
): Promise<void> {
  const updatePhoto: UpdatePhotoData = {
    ...genericUpdateFields(),
    ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
    Photo: photo,
    GroupID: group._id
  }

  return updateGroupOnServer(updatePhoto, group)
}

export async function updateGroupSettings(
  GroupSettings: GroupSettings,
  group: Model<GroupDoc>
): Promise<void> {
  if (isTrulyEmpty(group._id)) {
    Database.write(async () => {
      return group.update(() => {
        group.GroupSettings = GroupSettings
      })
    })
    return
  }

  const updateSettings: UpdateGroupSettingsData = {
    ...genericUpdateFields(),
    ActionType: GROUP_INFO_ACTION_TYPES.GROUP_SETTINGS,
    GroupSettings: GroupSettings,
    GroupID: group._id
  }

  return updateGroupOnServer(updateSettings, group)
}

export async function changeParticipantAdminStatus(
  participant: GroupParticipant,
  newType: GroupParticipantType,
  participantId: string,
  group: Model<GroupDoc> | GroupDoc
) {
  const Participants: Record<string, GroupParticipant> = {}
  const updatedParticipantStatus: GroupParticipant = {
    ...participant,
    Type: newType
  }

  Participants[participantId] = updatedParticipantStatus
  const data: UpdateParticipantData = {
    ...genericUpdateFields(),
    Participants: Participants,
    ActionType: GROUP_INFO_ACTION_TYPES.EDIT_PARTICIPANTS,
    GroupID: group._id ?? ''
  }

  return updateGroupOnServer(data, group)
}

export function addPendingParticipantToGroup(
  phone: string,
  group: Model<GroupDoc> | GroupDoc
) {
  const addPendingParticipantData: AddParticipantData = {
    ...genericUpdateFields(),
    PendingParticipants: {
      [phone]: {
        Status: GroupParticipantStatus.ACTIVE
      } as GroupParticipant
    },
    ActionType: GROUP_INFO_ACTION_TYPES.ADD_PARTICIPANTS,
    GroupID: group._id ?? ''
  }

  return updateGroupOnServer(addPendingParticipantData, group)
}

export function removePendingParticipantFromGroup(
  phone: string,
  group: Model<GroupDoc> | GroupDoc
) {
  const removePendingParticipantData: AddParticipantData = {
    ...genericUpdateFields(),
    PendingParticipants: {
      [phone]: {
        Status: GroupParticipantStatus.INACTIVE
      } as GroupParticipant
    },
    ActionType: GROUP_INFO_ACTION_TYPES.EDIT_PARTICIPANTS,
    GroupID: group._id ?? ''
  }

  return updateGroupOnServer(removePendingParticipantData, group)
}

export async function changeParticipantColor(
  participant: GroupParticipant,
  newColor: string,
  participantId: string,
  group: Model<GroupDoc> | GroupDoc
) {
  const Participants: Record<string, GroupParticipant> = {}
  const updatedParticipantStatus: GroupParticipant = {
    ...participant,
    MemberColor: newColor
  }

  Participants[participantId] = updatedParticipantStatus
  const data: UpdateParticipantData = {
    ...genericUpdateFields(),
    Participants: Participants,
    ActionType: GROUP_INFO_ACTION_TYPES.EDIT_PARTICIPANTS,
    GroupID: group._id ?? ''
  }

  return updateGroupOnServer(data, group)
}

export async function updateGroupOnServer(
  data: UpdateGroupData,
  group: Model<GroupDoc> | GroupDoc
) {
  const serverUpdate = await (
    await Api.post(
      `${API_GROUP_URL}${data.GroupID}`,
      sanitizeGroupInheritanceFields(data)
    )
  ).data

  const groupChangeDoc: Record<string, GroupDoc> = prepareDocForSaving({
    ...serverUpdate
  })

  const docs: ChangeDocsByTypes = {
    Group: groupChangeDoc,
    GroupEvent: {},
    Account: {},
    MasterLabel: {},
    Profile: {},
    UserSettings: {},
    Task: {},
    Note: {}
  }

  return await processDownloadedData(docs)
}

export async function getProfilesForParticipants(participantKeys: string[]) {
  if (participantKeys.length == 0) return

  try {
    const participantData = (await (
      await Api.post(`${API_PROFILES}`, {
        AccountIDs: participantKeys
      })
    ).data) as ParticipantDataDoc[]

    const existing = await ParticipantsForGroup(participantKeys).fetch()

    Database.write(async () => {
      if (existing.length != 0) {
        existing.map((existingParticipant) => {
          const participantFromServer = participantData.find(
            (participant) =>
              participant.AccountID == existingParticipant.AccountID
          )

          if (!participantFromServer) return

          existingParticipant.update((doc) => {
            doc.AccountID = participantFromServer.AccountID
            doc.FullName = participantFromServer.FullName
            doc.LastUpdate = participantFromServer.LastUpdate
            doc.PhoneNumber = participantFromServer.PhoneNumber
            doc.PhotoURL = participantFromServer.PhotoURL
          })
        })
      }

      participantData.forEach((participantFromServer) => {
        if (
          !existing.find(
            (existingParticipant) =>
              existingParticipant.AccountID === participantFromServer.AccountID
          )
        ) {
          Database.getCollection<Participant>(
            CollectionType.PARTICIPANT
          ).create((newParticipant) => {
            newParticipant.AccountID = participantFromServer.AccountID
            newParticipant.FullName = participantFromServer.FullName
            newParticipant.LastUpdate = participantFromServer.LastUpdate
            newParticipant.PhoneNumber = participantFromServer.PhoneNumber
            newParticipant.PhotoURL = participantFromServer.PhotoURL
          })
        }
      })
    })
  } catch (e) {
    Logger.red('Profiles:Error', e)
  }
}

export function buildRemoveParticipantData(
  participantToRemove: GroupParticipant,
  participantToRemoveId: string,
  groupId: string
) {
  const userId = LocalStorage.get(LocalStorageKey.USER_ID)!

  const leftParticipant: GroupParticipant = {
    ...participantToRemove,
    Status: GroupParticipantStatus.INACTIVE,
    Type: GroupParticipantType.REGULAR,
    RemovedBy: userId
  }

  const participants: Record<string, GroupParticipant> = {}
  participants[participantToRemoveId] = leftParticipant

  const leaveData: UpdateParticipantData = {
    Participants: participants,
    DeviceChangeID: LocalStorage.get(LocalStorageKey.DEVICE_ID),
    ActionType: GROUP_INFO_ACTION_TYPES.EDIT_PARTICIPANTS,
    UserID: userId,
    GroupID: groupId
  }
  return leaveData
}

export async function joinGroup(groupId: string, password?: string) {
  try {
    const response = Api.post(API_JOIN_GROUP, {
      groupId,
      password,
      DeviceChangeID: getOrUpdateDeviceId()
    })

    const joinedGroup: GroupDoc = (await response).data
    joinedGroup._sortOrder = dayjs().unix()
    const toUpdate: Record<string, GroupDoc> = {}
    toUpdate[joinedGroup._id ?? ''] = { ...joinedGroup }
    const groupToSave: ChangeDocsByTypes = {
      Group: toUpdate,
      GroupEvent: {},
      Account: {},
      MasterLabel: {},
      Profile: {},
      UserSettings: {},
      Task: {},
      Note: {}
    }

    processDownloadedData(groupToSave)

    doGetChanges(
      LocalStorage.get(LocalStorageKey.DEVICE_ID),
      [joinedGroup._id ?? ''],
      null
    )

    return joinedGroup
  } catch (e) {
    Logger.red('Error while joining group', e)
    return null
  }
}

export async function transferOwnershipOfAGroup(
  groupId: string,
  newOwnerId: string
) {
  const data = {
    ownerId: newOwnerId,
    DeviceChangeID: LocalStorage.get(LocalStorageKey.DEVICE_ID)
  }

  const response = await Api.post(API_TRANSFER_OWNERSHIP(groupId), data)
  const groupChangeDoc: Record<string, GroupDoc> = prepareDocForSaving({
    ...response.data
  })

  const docs: ChangeDocsByTypes = {
    Group: groupChangeDoc,
    GroupEvent: {},
    Account: {},
    MasterLabel: {},
    Profile: {},
    UserSettings: {},
    Task: {},
    Note: {}
  }

  return await processDownloadedData(docs)
}

export async function downgradeGroupOnServer(id: string) {
  const serverUpdate = await (
    await Api.post(API_DOWNGRADE_GROUP(id), {
      DeviceChangeID: getOrUpdateDeviceId()
    })
  ).data

  const groupChangeDoc: Record<string, GroupDoc> = prepareDocForSaving({
    ...serverUpdate
  })

  const docs: ChangeDocsByTypes = {
    Group: groupChangeDoc,
    GroupEvent: {},
    Account: {},
    MasterLabel: {},
    Profile: {},
    UserSettings: {},
    Task: {},
    Note: {}
  }

  return await processDownloadedData(docs)
}

export async function unlinkExternalCalendar(group: Model<GroupDoc>) {
  const provider = groupIdToProvider(group.CalendarEmail ?? '')

  if (provider === ProviderType.OUTLOOK) return Promise.resolve()

  return connectThirdPartyToServer({
    action: 'DISCONNECT',
    type: groupIdToProvider(group.CalendarEmail ?? '') ?? '',
    email: removePrefix(group.CalendarEmail ?? '')
  })
}

export interface ConnectThirdPartyToServerData {
  email: string
  username: string
  action: string
  connected: boolean
  expiry_date: number
  access_token: string
  account?: AccountDoc
}

export async function connectThirdPartyToServer(
  uploadData: Record<string, string>
): Promise<ConnectThirdPartyToServerData> {
  return (
    await Api.post(`/v1/services`, uploadData).then(async (data) => {
      Logger.pink('CONNECT:upload data', uploadData)
      Logger.pink('CONNECT:data from server', data)
      if (uploadData['action'] === 'CONNECT') {
        await Database.write(async () => {
          const acc = await Account()
          const connectedAccount = (data.data as ConnectThirdPartyToServerData)
            .account
          acc.update((local) => {
            if (connectedAccount) {
              local.ConnectedCalAccounts = connectedAccount.ConnectedCalAccounts
            }
          })
        })
      }
      return data
    })
  ).data
}

export async function searchPublicGroups(text: string) {
  if (is24meApp()) {
    return axios({
      method: 'get',
      url: '/dummy-url', // This URL is just a placeholder and won't be used
      validateStatus: () => true, // Always resolve the promise
      data: []
    }).then((response) => {
      return {
        status: 200,
        data: []
      }
    })
  }

  return await Api.get(`${API_SEARCH_GROUP}?search=${text}`)
}
